import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { redactString } from '../../lib/phi.js';

/**
 * Destination (Cerbo) draft preview — stage 3 of the browsable previews. Runs
 * the exporter's real column builders on the first N mapped rows and shows the
 * actual Cerbo table rows that WOULD be written, grouped by destination table,
 * WITHOUT writing anything. PT_ID + resolver-backed FKs (DX_ID / RX_ID /
 * TABLE_ID / VCID / DOCUMENT_ID) show as 0 here — they resolve against id_map /
 * the catalog at real import. Completes the hopper → FHIR cache → exporter view.
 */
export function CerboDraftView({
  sessionId,
  uploadId,
  spec,
  limit = 10,
  reveal = false,
}: {
  sessionId: string;
  uploadId: string;
  spec: Record<string, unknown>;
  limit?: number;
  /** Session-scoped, audit-logged PHI reveal (from SessionDetailPage). */
  reveal?: boolean;
}) {
  const run = useMutation({ mutationFn: () => api.cerboDraftFhirSpec(sessionId, { uploadId, spec, limit }) });

  return (
    <div className="space-y-2 text-[12px]">
      <div className="flex items-center gap-3">
        <button
          className="rounded px-2 py-1"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)', opacity: run.isPending ? 0.6 : 1 }}
          disabled={run.isPending}
          onClick={() => run.mutate()}
        >
          {run.isPending ? 'Building…' : `Draft first ${limit} rows`}
        </button>
        {run.data ? (
          <span className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>
            {run.data.okCount} resource{run.data.okCount === 1 ? '' : 's'} → {run.data.tables.length} Cerbo table{run.data.tables.length === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>real Cerbo columns; PT_ID + FK ids resolve at import · PHI {reveal ? 'visible' : 'redacted'}</span>
        )}
      </div>

      {run.error ? <div className="text-[11px]" style={{ color: 'var(--accent-red)' }}>Error: {String(run.error)}</div> : null}

      <div className="space-y-2">
        {(run.data?.tables ?? []).map((t) => {
          const cols = t.rows[0] ? Object.keys(t.rows[0].columns) : [];
          return (
            <div key={t.table} className="rounded-lg p-2" style={{ border: '1px solid var(--border)' }}>
              <div className="mb-1 font-mono text-[11px]" style={{ color: 'var(--accent-blue)' }}>
                {t.table} · {t.rows.length} row{t.rows.length === 1 ? '' : 's'}
              </div>
              <div className="overflow-x-auto">
                <table className="text-[10px] font-mono" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {cols.map((c) => (
                        <th key={c} className="px-1.5 py-0.5 text-left" style={{ color: 'var(--text-dimmed)', borderBottom: '1px solid var(--border)' }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.map((r) => (
                      <tr key={r.rowNumber}>
                        {cols.map((c) => (
                          <td key={c} className="px-1.5 py-0.5 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{redactCell(c, r.columns[c], reveal)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        {run.data && run.data.tables.length === 0 ? (
          <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>No rows produced — accept a spec and check the data preview.</div>
        ) : null}
      </div>
    </div>
  );
}

// Cerbo columns that carry identity PHI — whole-masked (mirrors redactJson's
// key-based masking on the FHIR cache view). Everything else gets the pattern
// pass (redactString catches emails/phones/DOBs/ids embedded in free-text like
// NOTE). The draft is a structural preview, so raw values live behind the
// audit-logged reveal toggle on the Data/Records views, not here.
const PHI_MASK_COL = /^(first|middle|last|nicknames?|dob|birth\w*|email\d*|phone\w*|address\w*|city|state|zip|ssn|pharm1|npi|dea)$/i;

function redactCell(col: string, v: unknown, reveal: boolean): string {
  if (v === null || v === undefined) return '∅';
  if (v === '') return "''";
  if (reveal) return String(v);
  if (PHI_MASK_COL.test(col)) return '[REDACTED]';
  return redactString(String(v));
}
